import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";

export default function VibratoLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/vibrato";
  const pageTitle = "Vibrato in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what vibrato is, how it changes pitch over time, how it differs from tremolo and chorus, and how vibrato is used in music production and instrument tone shaping.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "vibrato",
          "what is vibrato",
          "vibrato effect",
          "vibrato vs tremolo",
          "pitch modulation",
          "guitar vibrato",
          "audio vibrato",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Vibrato" },
          { "@type": "Thing", name: "Pitch modulation" },
          { "@type": "Thing", name: "Tremolo" },
          { "@type": "Thing", name: "Chorus" },
          { "@type": "Thing", name: "Modulation effect" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Vibrato",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Vibrato changes pitch over time, creating a subtle or dramatic wavering effect that adds motion and expressiveness.",
      icon: <MusicNoteRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Vibrato can make a sound feel more emotional, more human, and more alive without needing extra ambience or distortion.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Vocals, guitar, synths, strings, sound design, vintage keyboards, and many modulation-based production styles.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const vibratoTypes = [
    {
      name: "Performance Vibrato",
      description:
        "The natural pitch movement created by singers and instrumentalists for expression and phrasing.",
    },
    {
      name: "Guitar Vibrato",
      description:
        "Created by finger movement, string bending, or vibrato systems, often adding emotion and sustain to notes.",
    },
    {
      name: "Electronic Vibrato",
      description:
        "Pitch modulation created in synths, pedals, plugins, or studio processors.",
    },
    {
      name: "Tape-Style Vibrato",
      description:
        "A vintage-feeling pitch movement often associated with wow and flutter, lofi textures, and unstable analog playback.",
    },
    {
      name: "Stereo Vibrato",
      description:
        "Uses pitch movement and stereo behavior together for a wider, more animated effect.",
    },
    {
      name: "Tempo-Synced Vibrato",
      description:
        "Locks the modulation speed to the song so the pitch movement feels more rhythmic and intentional.",
    },
  ];

  const vibratoConcepts = [
    {
      name: "Pitch Modulation",
      description:
        "Vibrato works by moving pitch slightly above and below the original note over time.",
    },
    {
      name: "Rate",
      description:
        "Rate controls how fast the pitch moves. Slower rates feel smoother and more emotional, while faster rates can feel more obvious or stylized.",
    },
    {
      name: "Depth",
      description:
        "Depth controls how far the pitch moves. Small depth can sound natural, while heavy depth can become dramatic or seasick.",
    },
    {
      name: "Expressive Motion",
      description:
        "Because pitch is so central to musical feeling, even subtle vibrato can strongly affect how emotional or alive a part sounds.",
    },
  ];

  const controls = [
    {
      name: "Rate / Speed",
      description:
        "Controls how quickly the pitch modulation cycles over time.",
    },
    {
      name: "Depth / Amount",
      description:
        "Controls how strong the pitch movement is, from subtle shimmer to obvious wobble.",
    },
    {
      name: "Wave Shape",
      description:
        "Changes whether the pitch motion feels smooth, rounded, triangular, or more abrupt.",
    },
    {
      name: "Delay / Fade-In",
      description:
        "Lets the vibrato begin gradually after the note starts, which often sounds more natural and expressive.",
    },
    {
      name: "Stereo / Phase",
      description:
        "On some designs, controls how vibrato behaves between left and right channels for wider motion.",
    },
    {
      name: "Mix",
      description:
        "Blends processed and dry signal in tools that support parallel vibrato-style control.",
    },
  ];

  const commonUses = [
    "Adding vocal-style expression to synth leads and pads",
    "Making sustained guitar or keyboard notes feel more emotional",
    "Creating vintage pitch movement in lofi and retro textures",
    "Adding movement to static sustained sounds without using delay or reverb",
    "Creating seasick, warped, or dreamy sound design textures",
    "Adding character to solos, melodies, and exposed lead parts",
  ];

  const bestPractices = [
    "Use a lower depth first. Vibrato becomes obvious very quickly and can overwhelm the source if pushed too far.",
    "Choose the rate based on mood. Slower vibrato usually feels more expressive, while faster vibrato can feel more stylized or unstable.",
    "Use fade-in or delayed onset when available for a more natural entrance.",
    "Check whether the pitch movement supports the musical role of the part rather than distracting from it.",
    "Compare vibrato with chorus or tremolo if you want movement but are not sure which type of modulation best fits the sound.",
  ];

  const relatedLinks = [
    {
      title: "Tremolo",
      to: "/learning/tremolo",
      description:
        "Learn the key difference between tremolo and vibrato: tremolo changes volume, while vibrato changes pitch.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "See how chorus uses pitch-style modulation too, but usually aims more for width and doubling than pure vibrato does.",
    },
    {
      title: "Phaser",
      to: "/learning/phaser",
      description:
        "Explore another kind of motion effect that changes spectral movement rather than pitch.",
    },
    {
      title: "Flanger",
      to: "/learning/flanger",
      description:
        "Understand how flanger creates sweeping comb filtering instead of direct pitch wobble.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Vibrato | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Vibrato in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Vibrato is a pitch modulation effect that makes a note move slightly
                      sharp and flat over time. It is one of the most expressive forms of
                      musical motion because it changes pitch directly, which immediately affects
                      emotion, tension, and phrasing.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Vibrato can be subtle and natural, like a singer or violinist adding life
                      to a note, or it can be strong and stylized, as in psychedelic, warped, or
                      vintage production effects. It is simple in concept, but very powerful in feel.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5, lineHeight: 1.2 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    What is vibrato?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Vibrato is the repeated movement of pitch above and below a center note.
                    In natural performance, it is often used to make notes feel more expressive,
                    sustained, and emotionally alive. In audio processing, the same basic idea
                    can be created electronically with modulation tools.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Because vibrato affects pitch directly, it tends to feel more intimate and
                    more noticeable than some other modulation effects. Even a small amount can
                    make a sound feel less static and more human.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    In music production, vibrato is useful both as a natural expressive effect
                    and as a stylized texture. It can make a lead feel more emotional, a pad feel
                    more animated, or a sound design element feel unstable and dreamy.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    How vibrato works
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {vibratoConcepts.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Most electronic vibrato effects use an LFO to move the pitch repeatedly.
                    The speed of that LFO sets the rate, and the amount of pitch movement sets
                    the depth. Together, those settings determine whether the result feels natural,
                    vintage, psychedelic, or unstable.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Common types of vibrato
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {vibratoTypes.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Vibrato vs tremolo and chorus
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Different kinds of movement
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Vibrato changes pitch. Tremolo changes volume. Chorus usually uses pitch-style
                        modulation too, but its goal is often width, doubling, and shimmer rather than
                        isolated pure pitch wobble.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        In practical listening terms, vibrato tends to feel more exposed and more direct.
                        Chorus tends to feel wider and thicker. Tremolo tends to feel rhythmic and pulsing.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Common vibrato controls
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        How vibrato is used in production
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      Vibrato is especially useful when a sound feels too still. It can add a sense
                      of life and fragility without needing a bigger reverb or a louder level. On lead
                      sounds, it often adds emotion. On textures, it often adds atmosphere.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Best practices for using vibrato
                      </Typography>
                    </Stack>

                    <Stack spacing={1.15}>
                      {bestPractices.map((item, index) => (
                        <Stack key={index} direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WavesRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Why vibrato feels so expressive
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.35}>
                      {[
                        "It directly affects pitch, which the ear notices very quickly.",
                        "Even a small amount of movement can make a sustained note feel more human.",
                        "It can communicate tension, fragility, drama, or warmth depending on the settings.",
                        "It works as both a natural performance gesture and a stylized production effect.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WavesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Final thoughts
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Vibrato is one of the most emotionally direct modulation effects because it changes
                    pitch itself. It can make a sound feel more expressive, more human, more unstable,
                    or more dreamlike depending on how it is used.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Whether you want natural performance-style movement or obvious stylized pitch motion,
                    vibrato is a valuable tool because it adds life to sounds that might otherwise feel too static.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}